import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackgroundSlideshow from "../components/BackgroundSlideshow";
import ProductCard from "../components/ProductCard";
import ReviewsSection from "../components/ReviewsSection";
import "../css/Home.css";
import "../css/global.css";
import { useContacts } from "../context/ContactsContext";
import API from "../config/api";
import reviews from "../data/reviews";

// Icon paths from public/assets
const checkmarkIcon = "/assets/icons/checkmark.svg";
const windowIcon = "/assets/icons/window.png";
const doorIcon = "/assets/icons/door.png";
const blindsIcon = "/assets/icons/blinds.png";
const gateIcon = "/assets/icons/gate.png";
const phoneIcon = "/assets/icons/call.png";
const emailIcon = "/assets/icons/email.png";
const emailIcon2 = "/assets/icons/mail.svg";

const Home = () => {
  const [slideshowImages, setSlideshowImages] = useState([]);
  const { contacts } = useContacts();

  // Fetch slideshow images from PHP backend
  useEffect(() => {
    const fetchImages = async () => {
      try {
        // Fetch slideshow images
        const slideshowResponse = await fetch(API.ENDPOINTS.IMAGES('slideshow'));
        const slideshowData = await slideshowResponse.json();
        if (slideshowData.success && slideshowData.images) {
          setSlideshowImages(slideshowData.images.map(img => `${API.ASSETS_URL}${img}`));
        }
      } catch (error) {
        // Silently handle fetch errors
      }
    };

    fetchImages();
  }, []);

  // Static background images
  const visualPlaceholderBg = "/assets/BgImages/finestra_13.jpg";
  const promoSectionBg = "/assets/BgImages/veranda_2.jpg";

  return (
    <div className="home-page">
      <Navbar />
      {slideshowImages.length > 0 && <BackgroundSlideshow images={slideshowImages} />}

      {/* Hero Section with Logo and Slogan */}
      <section className="hero-section">
        <div className="hero-overlay-gradient"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="hero-title-line">Eccellenza</span>
            <span className="hero-title-line">in ogni dettaglio</span>
          </h1>
          <p className="hero-slogan">
            Dal 1967, la sicurezza e lo stile che la tua casa merita.
            <br />
            Serramenti in alluminio, ferro, PVC e legno.
          </p>
          <div className="hero-actions">
            <a href="/contatti" className="btn btn--primary hero-btn">
              Richiedi Preventivo
            </a>
            <Link to="/prodotti" className="btn hero-btn btn--outline-white">
              I Nostri Prodotti
            </Link>
          </div>
        </div>

        <a
          href="#about"
          className="scroll-indicator"
          aria-label="Scorri in basso"
          onClick={(e) => {
            e.preventDefault();
            const target = document.getElementById("about");
            if (target) {
              target.scrollIntoView({ behavior: "smooth" });
            }
          }}
        >
          <span className="scroll-text">Scopri di più</span>
          <span className="scroll-line"></span>
        </a>
      </section>

      {/* Chi Siamo Section - Modern Split Layout */}
      <section id="about" className="section-animate about-section">
        <div className="section-content about-container">
          <div className="about-visual">
            <div className="visual-card main-visual">
              <div 
                className="visual-placeholder"
                style={{ backgroundImage: `url(${visualPlaceholderBg}), linear-gradient(45deg, #333, #555)` }}
              >
                <span className="year-badge">Dal 1967</span>
              </div>
            </div>
          </div>
          <div className="about-info">
            <h4 className="section-subtitle">CHI SIAMO</h4>
            <h2 className="section-title">Tradizione e Innovazione</h2>
            <p className="about-description">
              <strong>FER-AL</strong> rappresenta un punto di riferimento nel
              settore dei serramenti. Con oltre 50 anni di storia, uniamo
              l'artigianalità della lavorazione del ferro e dell'alluminio alle
              più moderne tecnologie del PVC e del legno.
            </p>
            <p className="about-description">
              Non vendiamo solo prodotti, ma soluzioni abitative che migliorano
              il comfort, la sicurezza e l'efficienza energetica della vostra
              casa.
            </p>

            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-icon-box">
                  <img src={checkmarkIcon} alt="" />
                </div>
                <div>
                  <h5>Qualità Certificata</h5>
                  <p>Materiali selezionati e garantiti</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon-box">
                  <img src={checkmarkIcon} alt="" />
                </div>
                <div>
                  <h5>Posa Qualificata</h5>
                  <p>Installazione a regola d'arte</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon-box">
                  <img src={checkmarkIcon} alt="" />
                </div>
                <div>
                  <h5>Assistenza Completa</h5>
                  <p>Dal preventivo al post-vendita</p>
                </div>
              </div>
            </div>

            <a href="/about" className="text-link">
              Scopri la nostra storia <span className="arrow">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* Prodotti Section - Modern Cards */}
      <section id="prodotti" className="section-animate products-section">
        <div className="section-content">
          <div className="section-header">
            <h4 className="section-subtitle">LE NOSTRE SOLUZIONI</h4>
            <h2 className="section-title">Prodotti su misura</h2>
          </div>

          <div className="products-grid-modern">
            <ProductCard
              to="/prodotti?category=finestre"
              icon={windowIcon}
              title="Finestre"
              description="Design e isolamento termico per il massimo comfort abitativo."
            />

            <ProductCard
              to="/prodotti?category=porte"
              icon={doorIcon}
              title="Porte"
              description="Sicurezza ed eleganza per l'ingresso e gli interni della tua casa."
            />

            <ProductCard
              to="/prodotti?category=tende"
              icon={blindsIcon}
              title="Tende"
              description="Sistemi oscuranti e schermature solari di ultima generazione."
            />

            <ProductCard
              to="/prodotti?category=altro"
              icon={gateIcon}
              title="Complementi"
              description="Verande, cancelli e lavorazioni speciali in ferro e alluminio."
            />
          </div>
        </div>
      </section>

      {/* Promo Strip - Parallax Style */}
      <section 
        className="section-animate promo-section"
        style={{ backgroundImage: `url(${promoSectionBg})` }}
      >
        <div className="promo-overlay"></div>
        <div className="section-content promo-content">
          <div className="promo-text">
            <h2>Rinnova la tua casa oggi</h2>
            <p>
              Approfitta delle detrazioni fiscali e delle nostre offerte
              esclusive.
            </p>
          </div>
          <div className="promo-actions">
            <a href="/offerte" className="btn btn--white">
              Vedi Offerte
            </a>
            <a href="/agevolazioni" className="btn btn--outline-white">
              Bonus Fiscali 2025
            </a>
          </div>
        </div>
      </section>

      <section className="section-animate stats-section">
        <div className="section-content">
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">50+</span>
              <span className="stat-label">Anni di Esperienza</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">100%</span>
              <span className="stat-label">Made in Italy</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">1000+</span>
              <span className="stat-label">Clienti Soddisfatti</span>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection reviews={reviews} />

      {/* Contact CTA Section */}
      <section className="section-animate contact-cta-section">
        <div className="section-content contact-layout">
          <div className="contact-text-area">
            <h4 className="section-subtitle">PARLIAMONE</h4>
            <h2 className="section-title">Hai un progetto in mente?</h2>
            <p>
              Siamo qui per ascoltare le tue esigenze e proporti la soluzione
              migliore. Richiedi un sopralluogo o un preventivo gratuito senza
              impegno.
            </p>
            <div className="contact-direct-info">
              <div className="info-row">
                <img src={phoneIcon} alt="Telefono" />
                <span>{contacts.PHONE_DISPLAY}</span>
              </div>
              <div className="info-row">
                <img src={emailIcon} alt="Email" />
                <span>{contacts.CONTACT_EMAIL}</span>
              </div>
            </div>
          </div>
          <div className="contact-action-area">
            <a href="/contatti" className="cta-box-link">
              <div className="cta-box">
                <img className="cta-icon" src={emailIcon2} alt="email" />
                <h3>Richiedi Preventivo</h3>
                <p>Compila il modulo online</p>
                <span className="fake-btn">Vai al form →</span>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
